<template>
  <div class="ide-page">

    <!-- Header -->
    <div style="display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:20px; flex-wrap:wrap; gap:12px;">
      <div>
        <h2 style="font-size:18px; font-weight:800; color:var(--text-heading); letter-spacing:-0.3px; margin-bottom:4px;">Tienda Online</h2>
        <p style="font-size:12px; color:var(--text-faint);">Catálogo de la tienda que el agente IA manda por WhatsApp — el cliente arma su pedido y vuelve como mensaje a la conversación</p>
      </div>
      <button v-if="tab === 'articulos'" class="btn-add" @click="abrirForm(null)">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Agregar artículo
      </button>
      <button v-else-if="tab === 'categorias'" class="btn-add" @click="abrirFormCategoria(null)">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Agregar categoría
      </button>
      <button v-else-if="tab === 'promociones'" class="btn-add" @click="abrirFormPromocion(null)">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Agregar promoción
      </button>
      <button v-else class="btn-add" @click="abrirFormSucursal(null)">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Agregar sucursal
      </button>
    </div>

    <!-- Panel: activar tienda / portada / color -->
    <div class="ide-ia-card" style="padding:16px 18px; margin-bottom:20px;">
      <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap; justify-content:space-between;">
        <div style="display:flex; align-items:center; gap:10px;">
          <div class="ide-toggle" :class="{ 'ide-toggle--on': cliente.tiendaActiva }" @click="toggleTienda"><div></div></div>
          <div>
            <div style="font-size:13px; font-weight:700; color:var(--text-primary);">{{ cliente.tiendaActiva ? 'Tienda activa' : 'Tienda desactivada' }}</div>
            <div style="font-size:11px; color:var(--text-faint);">{{ cliente.tiendaActiva ? 'El agente ya puede mandar el link con la herramienta "Abrir Tienda Online"' : 'Activala para que el agente pueda mandar el catálogo online' }}</div>
          </div>
        </div>
        <div v-if="cliente.tiendaActiva && cliente.slug" style="display:flex; align-items:center; gap:8px;">
          <code style="font-size:11px; background:var(--bg-page); padding:6px 10px; border-radius:6px; color:var(--text-muted);">/tienda/{{ cliente.slug }}</code>
          <button @click="copiarLink" style="background:none; border:1px solid var(--border); border-radius:8px; color:var(--text-faint); padding:6px 10px; font-size:11px; cursor:pointer; font-family:inherit;">Copiar link</button>
        </div>
      </div>

      <div style="margin-top:14px; padding-top:14px; border-top:1px solid var(--border); display:flex; align-items:center; gap:24px; flex-wrap:wrap;">
        <div style="display:flex; align-items:center; gap:14px;">
          <div style="width:120px; height:64px; border-radius:8px; overflow:hidden; background:var(--bg-page); flex-shrink:0; display:flex; align-items:center; justify-content:center;">
            <img v-if="cliente.tiendaPortadaUrl" :src="cliente.tiendaPortadaUrl" style="width:100%; height:100%; object-fit:cover;" />
            <span v-else style="font-size:10px; color:var(--text-disabled);">Sin portada</span>
          </div>
          <label class="btn-add" style="background:none; border:1px solid var(--border); color:var(--text-faint); cursor:pointer;">
            <input type="file" accept="image/*" style="display:none;" @change="subirPortada" />
            {{ subiendoPortada ? 'Subiendo…' : 'Subir portada' }}
          </label>
        </div>

        <div style="display:flex; align-items:center; gap:10px;">
          <input type="color" v-model="colorPrimario" @change="guardarColor" style="width:40px; height:32px; border:1px solid var(--border); border-radius:6px; cursor:pointer; padding:2px; background:var(--bg-page);" />
          <div>
            <div style="font-size:12px; font-weight:700; color:var(--text-primary);">Color primario</div>
            <div style="font-size:10px; color:var(--text-faint);">Se usa en toda la tienda pública</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div style="display:flex; gap:6px; margin-bottom:18px; border-bottom:1px solid var(--border);">
      <button v-for="t in [{id:'articulos',label:'Artículos'},{id:'categorias',label:'Categorías'},{id:'promociones',label:'Promociones'},{id:'pedidos',label:'Pedidos'},{id:'sucursales',label:'Sucursales'}]" :key="t.id" @click="tab = t.id" :style="tabStyle(t.id)">{{ t.label }}</button>
    </div>

    <!-- ══════════════ TAB: ARTÍCULOS ══════════════ -->
    <template v-if="tab === 'articulos'">
      <div v-if="loading" style="display:flex; justify-content:center; padding:56px;">
        <v-progress-circular indeterminate color="primary" size="36" />
      </div>

      <div v-else-if="!articulos.length" class="ide-ia-card" style="text-align:center; padding:56px; color:var(--text-disabled);">
        <div style="font-size:36px; margin-bottom:12px;">🛍️</div>
        <div style="font-size:14px; font-weight:700; color:var(--text-muted); margin-bottom:6px;">Sin artículos</div>
        <div style="font-size:12px;">Agrega el primer artículo de tu tienda online</div>
      </div>

      <div v-else style="display:grid; grid-template-columns:repeat(auto-fill, minmax(220px, 1fr)); gap:12px;">
        <div v-for="a in articulos" :key="a.id" class="ide-ia-card" style="padding:0; overflow:hidden; cursor:pointer;" :style="{ opacity: a.activo ? 1 : 0.55 }" @click="abrirForm(a)">
          <div style="width:100%; height:120px; background:var(--bg-page); display:flex; align-items:center; justify-content:center; overflow:hidden; position:relative;">
            <img v-if="a.imagenUrl" :src="a.imagenUrl" style="width:100%; height:100%; object-fit:cover;" />
            <span v-else style="font-size:28px;">📦</span>
            <span v-if="promoVigentePorArticulo[a.id]" style="position:absolute; top:6px; left:6px; background:#f97316; color:#fff; font-size:9px; font-weight:800; padding:2px 6px; border-radius:4px;">🔥 OFERTA</span>
          </div>
          <div style="padding:10px 12px;">
            <div v-if="a.categoria" style="font-size:10px; color:#818cf8; margin-bottom:2px;">{{ a.categoria }}</div>
            <div style="font-size:13px; font-weight:700; color:var(--text-heading); margin-bottom:4px;">{{ a.nombre }}</div>
            <div style="display:flex; align-items:center; justify-content:space-between;">
              <span style="font-size:13px; font-weight:700; color:var(--text-primary);">{{ a.moneda }} {{ Number(a.precio).toFixed(2) }}</span>
              <span v-if="a.gruposOpciones && a.gruposOpciones.length" style="font-size:10px; color:var(--text-faint);">{{ a.gruposOpciones.length }} grupo(s)</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ══════════════ TAB: CATEGORÍAS ══════════════ -->
    <template v-else-if="tab === 'categorias'">
      <div v-if="!categoriasTienda.length" class="ide-ia-card" style="text-align:center; padding:56px; color:var(--text-disabled);">
        <div style="font-size:36px; margin-bottom:12px;">🗂️</div>
        <div style="font-size:14px; font-weight:700; color:var(--text-muted); margin-bottom:6px;">Sin categorías</div>
        <div style="font-size:12px;">Las categorías con imagen se ven como tabs con thumbnail en la tienda pública</div>
      </div>
      <div v-else style="display:grid; grid-template-columns:repeat(auto-fill, minmax(180px, 1fr)); gap:12px;">
        <div v-for="c in categoriasTienda" :key="c.id" class="ide-ia-card" style="padding:0; overflow:hidden; cursor:pointer;" :style="{ opacity: c.activo ? 1 : 0.55 }" @click="abrirFormCategoria(c)">
          <div style="width:100%; height:90px; background:var(--bg-page); display:flex; align-items:center; justify-content:center; overflow:hidden;">
            <img v-if="c.imagenUrl" :src="c.imagenUrl" style="width:100%; height:100%; object-fit:cover;" />
            <span v-else style="font-size:24px;">🖼️</span>
          </div>
          <div style="padding:10px 12px;">
            <div style="font-size:13px; font-weight:700; color:var(--text-heading);">{{ c.nombre }}</div>
            <div style="font-size:10px; color:var(--text-faint);">orden {{ c.orden }}</div>
          </div>
        </div>
      </div>
    </template>

    <!-- ══════════════ TAB: PROMOCIONES ══════════════ -->
    <template v-else-if="tab === 'promociones'">
      <div v-if="!promociones.length" class="ide-ia-card" style="text-align:center; padding:56px; color:var(--text-disabled);">
        <div style="font-size:36px; margin-bottom:12px;">🔥</div>
        <div style="font-size:14px; font-weight:700; color:var(--text-muted); margin-bottom:6px;">Sin promociones</div>
        <div style="font-size:12px;">Ofertas con precio y vigencia limitada para un artículo puntual</div>
      </div>
      <div v-else style="display:flex; flex-direction:column; gap:10px;">
        <div v-for="p in promociones" :key="p.id" class="ide-ia-card" style="padding:14px 16px; cursor:pointer;" @click="abrirFormPromocion(p)">
          <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap;">
            <div>
              <div style="display:flex; align-items:center; gap:8px; margin-bottom:4px;">
                <span style="font-size:13px; font-weight:700; color:var(--text-heading);">{{ nombreArticulo(p.articuloId) }}</span>
                <span :style="estadoPromoBadgeStyle(p)">{{ estadoPromoLabel(p) }}</span>
                <span v-if="p.sucursalId" style="font-size:10px; color:#818cf8;">📍 {{ nombreSucursal(p.sucursalId) }}</span>
                <span v-else-if="sucursalesTienda.length" style="font-size:10px; color:var(--text-faint);">Todas las sucursales</span>
              </div>
              <div style="font-size:11px; color:var(--text-faint);">{{ formatearFecha(p.fechaInicio) }} → {{ formatearFecha(p.fechaFin) }}</div>
            </div>
            <div style="text-align:right;">
              <div style="font-size:13px; font-weight:800; color:#f97316;">Bs {{ Number(p.precioPromocional).toFixed(2) }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ══════════════ TAB: SUCURSALES ══════════════ -->
    <template v-else>
      <div v-if="!sucursalesTienda.length" class="ide-ia-card" style="text-align:center; padding:56px; color:var(--text-disabled);">
        <div style="font-size:36px; margin-bottom:12px;">📍</div>
        <div style="font-size:14px; font-weight:700; color:var(--text-muted); margin-bottom:6px;">Sin sucursales</div>
        <div style="font-size:12px;">Un solo local — sin sucursales, la tienda funciona igual que siempre, sin selector para el cliente</div>
      </div>
      <div v-else style="display:flex; flex-direction:column; gap:10px;">
        <div v-for="s in sucursalesTienda" :key="s.id" class="ide-ia-card" style="padding:14px 16px; cursor:pointer;" :style="{ opacity: s.activo ? 1 : 0.55 }" @click="abrirFormSucursal(s)">
          <div style="display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;">
            <div>
              <div style="display:flex; align-items:center; gap:8px; margin-bottom:4px;">
                <span style="font-size:13px; font-weight:700; color:var(--text-heading);">{{ s.nombre }}</span>
                <span style="font-size:10px; color:#818cf8; background:#818cf822; padding:2px 8px; border-radius:4px; font-weight:700;">{{ s.codigo }}</span>
              </div>
              <div style="font-size:11px; color:var(--text-faint);">{{ s.direccion || 'Sin dirección cargada' }}</div>
            </div>
            <div style="display:flex; gap:6px;">
              <span v-if="s.aceptaPagoQr" style="font-size:10px; color:var(--text-faint);">💳 QR</span>
              <span v-if="s.aceptaPagoEfectivo" style="font-size:10px; color:var(--text-faint);">💵 Efectivo</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ══════════════ TAB: PEDIDOS ══════════════ -->
    <template v-if="tab === 'pedidos'">
      <div style="display:flex; gap:8px; margin-bottom:18px;">
        <button @click="filtroPedidos = null" :style="filtroPedidosStyle(null)">Todos</button>
        <button @click="filtroPedidos = 'pendiente'" :style="filtroPedidosStyle('pendiente')">Pendientes</button>
        <button @click="filtroPedidos = 'confirmado'" :style="filtroPedidosStyle('confirmado')">Confirmados</button>
        <button @click="filtroPedidos = 'entregado'" :style="filtroPedidosStyle('entregado')">Entregados</button>
      </div>
      <div style="display:flex; justify-content:center; padding:40px;">
        <router-link :to="{ name: 'pedidos' }" style="background:#818cf8; color:white; border:none; border-radius:6px; padding:12px 24px; cursor:pointer; font-family:inherit; text-decoration:none; font-weight:600;">
          Ver todos los pedidos →
        </router-link>
      </div>
    </template>

    <!-- Modal: crear/editar artículo -->
    <div v-if="dialogForm" class="cat-overlay" @click.self="dialogForm = false">
      <div class="cat-modal">
        <div style="display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid var(--border); flex-shrink:0;">
          <span style="font-size:15px; font-weight:700; color:var(--text-heading);">{{ editando ? 'Editar artículo' : 'Nuevo artículo' }}</span>
          <div style="display:flex; align-items:center; gap:10px;">
            <button v-if="editando" @click="confirmarEliminar" style="background:none; border:none; cursor:pointer; color:#ef4444; font-size:12px; font-family:inherit; display:flex; align-items:center; gap:4px;">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
              Eliminar
            </button>
            <button @click="dialogForm = false" style="background:none; border:none; cursor:pointer; color:var(--text-disabled); padding:4px; line-height:0; display:flex;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <div style="overflow-y:auto; padding:18px 22px; flex:1;">
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:16px;">
            <div class="ide-field" style="grid-column:span 2;">
              <label>Nombre *</label>
              <input v-model="form.nombre" class="ide-input" placeholder="Ej: Vestido Corte A, Balón N°5, Mochila Escolar…" />
            </div>
            <div class="ide-field">
              <label>Categoría</label>
              <select v-if="categoriasTienda.length" v-model="form.categoria" class="ide-select">
                <option value="">Sin categoría</option>
                <option v-for="c in opcionesCategoriaForm" :key="c" :value="c">{{ c }}</option>
              </select>
              <div v-else style="font-size:11px; color:var(--text-disabled); padding:8px 0;">
                Todavía no creaste categorías —
                <a href="#" @click.prevent="tab = 'categorias'" style="color:#818cf8;">creá una acá</a>
              </div>
              <span v-if="form.categoria && !categoriasTienda.some(c => c.nombre === form.categoria)" style="font-size:10px; color:#f59e0b;">
                "{{ form.categoria }}" no existe como categoría creada — se guarda igual, pero no va a tener imagen en la tienda hasta que la crees en la pestaña Categorías con ese mismo nombre.
              </span>
            </div>
            <div class="ide-field">
              <label>Moneda</label>
              <input v-model="form.moneda" class="ide-input" placeholder="Bs" />
            </div>
            <div class="ide-field">
              <label>Precio base *</label>
              <input v-model.number="form.precio" type="number" min="0" step="0.01" class="ide-input" placeholder="0.00" />
            </div>
            <div class="ide-field" style="flex-direction:row; align-items:center; gap:10px;">
              <div class="ide-toggle" :class="{ 'ide-toggle--on': form.activo }" @click="form.activo = !form.activo"><div></div></div>
              <span style="font-size:12px;" :style="{ color: form.activo ? '#22c55e' : 'var(--text-faint)' }">{{ form.activo ? 'Activo' : 'Inactivo' }}</span>
            </div>
            <div class="ide-field" style="grid-column:span 2;">
              <label>Descripción</label>
              <textarea v-model="form.descripcion" class="ide-textarea" rows="2" placeholder="Detalle del artículo…"></textarea>
            </div>
          </div>

          <!-- Imagen -->
          <div v-if="editando" style="border-top:1px solid var(--border); padding-top:14px; margin-bottom:14px;">
            <div style="font-size:12px; font-weight:700; color:var(--text-primary); margin-bottom:10px;">Imagen</div>
            <div style="display:flex; align-items:center; gap:12px;">
              <div style="width:80px; height:80px; border-radius:8px; overflow:hidden; background:var(--bg-page); flex-shrink:0;">
                <img v-if="editando.imagenUrl" :src="editando.imagenUrl" style="width:100%; height:100%; object-fit:cover;" />
              </div>
              <label class="img-upload-zone" style="flex:1;">
                <input type="file" accept="image/*" style="display:none;" @change="subirImagenArticulo" />
                <span style="font-size:12px; color:var(--text-faint);">{{ subiendoImagen ? 'Subiendo…' : 'Seleccionar imagen (se ajusta a 600×600 automático)' }}</span>
              </label>
            </div>
          </div>
          <div v-else style="margin-bottom:14px; padding:10px; background:var(--bg-page); border-radius:8px; font-size:11px; color:var(--text-disabled); text-align:center;">
            Guarda el artículo primero para luego subir la imagen
          </div>

          <!-- Grupos de opciones -->
          <div style="border-top:1px solid var(--border); padding-top:14px;">
            <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:10px;">
              <div style="font-size:12px; font-weight:700; color:var(--text-primary);">Grupos de opciones</div>
              <button class="btn-add" style="padding:5px 10px; font-size:11px;" @click="agregarGrupo">+ Agregar grupo</button>
            </div>
            <div v-if="!form.gruposOpciones.length" style="font-size:11px; color:var(--text-disabled); margin-bottom:8px;">Sin variantes — el cliente lo agrega directo al carrito.</div>

            <div v-for="(g, gi) in form.gruposOpciones" :key="g.id" class="ide-ia-card" style="padding:12px 14px; margin-bottom:10px;">
              <div style="display:grid; grid-template-columns:1.5fr 1fr auto; gap:8px; align-items:center; margin-bottom:8px;">
                <input v-model="g.nombre" class="ide-input" placeholder="Ej: Color, Talla…" style="padding:6px 10px; font-size:12px;" />
                <select v-model="g.tipo" class="ide-select" style="padding:6px 10px; font-size:12px;">
                  <option value="unica">Una opción</option>
                  <option value="multiple">Varias opciones</option>
                </select>
                <button @click="form.gruposOpciones.splice(gi, 1)" style="background:none; border:none; cursor:pointer; color:#ef4444;" title="Quitar grupo">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <label style="display:flex; align-items:center; gap:6px; font-size:11px; color:var(--text-faint); margin-bottom:10px; cursor:pointer;">
                <input type="checkbox" v-model="g.obligatorio" /> Obligatorio
              </label>

              <div v-for="(o, oi) in g.opciones" :key="o.id" style="display:grid; grid-template-columns:1.5fr 1fr auto; gap:8px; margin-bottom:6px;">
                <input v-model="o.nombre" class="ide-input" placeholder="Ej: Azul, Talla M…" style="padding:5px 10px; font-size:11px;" />
                <input v-model.number="o.precioExtra" type="number" step="0.01" class="ide-input" placeholder="Recargo" style="padding:5px 10px; font-size:11px;" />
                <button @click="g.opciones.splice(oi, 1)" style="background:none; border:none; cursor:pointer; color:var(--text-disabled);">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <button @click="agregarOpcion(g)" style="background:none; border:none; cursor:pointer; color:#818cf8; font-size:11px; font-family:inherit; margin-top:4px;">+ Agregar opción</button>
            </div>
          </div>

          <!-- Disponibilidad por sucursal -->
          <div v-if="editando && sucursalesTienda.length" style="border-top:1px solid var(--border); padding-top:14px; margin-top:14px;">
            <div style="font-size:12px; font-weight:700; color:var(--text-primary); margin-bottom:10px;">Disponibilidad por sucursal</div>
            <div v-for="fila in disponibilidad" :key="fila.sucursalId" style="display:grid; grid-template-columns:1fr auto 110px; gap:10px; align-items:center; margin-bottom:8px;">
              <span style="font-size:12px; color:var(--text-muted);">{{ nombreSucursal(fila.sucursalId) }}</span>
              <div class="ide-toggle" :class="{ 'ide-toggle--on': fila.activo }" @click="fila.activo = !fila.activo"><div></div></div>
              <input v-model.number="fila.stock" type="number" min="0" class="ide-input" placeholder="Sin límite" style="padding:5px 8px; font-size:11px;" />
            </div>
            <button @click="guardarDisponibilidad" :disabled="savingDisponibilidad" style="background:none; border:1px solid var(--border); border-radius:8px; color:var(--text-faint); padding:6px 12px; font-size:11px; cursor:pointer; font-family:inherit; margin-top:4px;">
              {{ savingDisponibilidad ? 'Guardando…' : 'Guardar disponibilidad' }}
            </button>
          </div>
        </div>

        <div style="display:flex; justify-content:flex-end; gap:8px; padding:12px 22px 18px; border-top:1px solid var(--border); flex-shrink:0;">
          <button @click="dialogForm = false" style="background:none; border:1px solid var(--border); border-radius:8px; color:var(--text-faint); padding:8px 16px; font-size:12px; cursor:pointer; font-family:inherit;">Cancelar</button>
          <button @click="guardar" :disabled="saving" style="background:#6366f1; color:#fff; border:none; border-radius:8px; padding:8px 18px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; display:flex; align-items:center; gap:6px;">
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
            {{ editando ? 'Guardar cambios' : 'Crear artículo' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: eliminar artículo -->
    <div v-if="dialogEliminar" class="cat-overlay" @click.self="dialogEliminar = false">
      <div style="background:var(--bg-surface); border:1px solid var(--border); border-radius:12px; width:420px; max-width:95%; padding:24px;">
        <div style="font-size:15px; font-weight:700; color:var(--text-heading); margin-bottom:8px;">Eliminar artículo</div>
        <div style="font-size:12px; color:var(--text-muted); margin-bottom:20px;">¿Eliminar <strong style="color:var(--text-heading);">{{ editando ? editando.nombre : '' }}</strong>?</div>
        <div style="display:flex; justify-content:flex-end; gap:8px;">
          <button @click="dialogEliminar = false" style="background:none; border:1px solid var(--border); border-radius:8px; color:var(--text-faint); padding:8px 16px; font-size:12px; cursor:pointer; font-family:inherit;">Cancelar</button>
          <button @click="eliminar" style="background:#ef4444; color:#fff; border:none; border-radius:8px; padding:8px 16px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit;">Eliminar</button>
        </div>
      </div>
    </div>

    <!-- Modal: crear/editar categoría -->
    <div v-if="dialogCategoria" class="cat-overlay" @click.self="dialogCategoria = false">
      <div style="background:var(--bg-surface); border:1px solid var(--border); border-radius:12px; width:420px; max-width:95%; padding:22px;">
        <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:16px;">
          <span style="font-size:14px; font-weight:700; color:var(--text-heading);">{{ editandoCategoria ? 'Editar categoría' : 'Nueva categoría' }}</span>
          <button v-if="editandoCategoria" @click="eliminarCategoria" style="background:none; border:none; cursor:pointer; color:#ef4444; font-size:11px; font-family:inherit;">Eliminar</button>
        </div>
        <div class="ide-field" style="margin-bottom:12px;">
          <label>Nombre *</label>
          <input v-model="formCategoria.nombre" class="ide-input" placeholder="Ej: Pollo Broasted" />
        </div>
        <div class="ide-field" style="margin-bottom:14px;">
          <label>Orden</label>
          <input v-model.number="formCategoria.orden" type="number" class="ide-input" />
        </div>
        <div v-if="editandoCategoria" style="display:flex; align-items:center; gap:12px; margin-bottom:14px;">
          <div style="width:64px; height:64px; border-radius:8px; overflow:hidden; background:var(--bg-page); flex-shrink:0;">
            <img v-if="editandoCategoria.imagenUrl" :src="editandoCategoria.imagenUrl" style="width:100%; height:100%; object-fit:cover;" />
          </div>
          <label class="img-upload-zone" style="flex:1;">
            <input type="file" accept="image/*" style="display:none;" @change="subirImagenCategoria" />
            <span style="font-size:11px; color:var(--text-faint);">{{ subiendoImagenCategoria ? 'Subiendo…' : 'Seleccionar imagen (300×300 automático)' }}</span>
          </label>
        </div>
        <div v-else style="margin-bottom:14px; padding:10px; background:var(--bg-page); border-radius:8px; font-size:11px; color:var(--text-disabled); text-align:center;">
          Guarda la categoría primero para luego subir la imagen
        </div>
        <div style="display:flex; justify-content:flex-end; gap:8px;">
          <button @click="dialogCategoria = false" style="background:none; border:1px solid var(--border); border-radius:8px; color:var(--text-faint); padding:8px 16px; font-size:12px; cursor:pointer; font-family:inherit;">Cancelar</button>
          <button @click="guardarCategoria" :disabled="savingCategoria" style="background:#6366f1; color:#fff; border:none; border-radius:8px; padding:8px 16px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit;">{{ editandoCategoria ? 'Guardar' : 'Crear' }}</button>
        </div>
      </div>
    </div>

    <!-- Modal: crear/editar promoción -->
    <div v-if="dialogPromocion" class="cat-overlay" @click.self="dialogPromocion = false">
      <div style="background:var(--bg-surface); border:1px solid var(--border); border-radius:12px; width:420px; max-width:95%; padding:22px;">
        <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:16px;">
          <span style="font-size:14px; font-weight:700; color:var(--text-heading);">{{ editandoPromocion ? 'Editar promoción' : 'Nueva promoción' }}</span>
          <button v-if="editandoPromocion" @click="eliminarPromocion" style="background:none; border:none; cursor:pointer; color:#ef4444; font-size:11px; font-family:inherit;">Eliminar</button>
        </div>
        <div class="ide-field" style="margin-bottom:12px;">
          <label>Artículo *</label>
          <select v-model="formPromocion.articuloId" class="ide-select" :disabled="!!editandoPromocion">
            <option value="" disabled>Elegí un artículo…</option>
            <option v-for="a in articulos" :key="a.id" :value="a.id">{{ a.nombre }} ({{ a.moneda }} {{ Number(a.precio).toFixed(2) }})</option>
          </select>
        </div>
        <div v-if="sucursalesTienda.length" class="ide-field" style="margin-bottom:12px;">
          <label>Sucursal</label>
          <select v-model="formPromocion.sucursalId" class="ide-select">
            <option value="">Todas las sucursales</option>
            <option v-for="s in sucursalesTienda" :key="s.id" :value="s.id">{{ s.nombre }}</option>
          </select>
        </div>
        <div class="ide-field" style="margin-bottom:12px;">
          <label>Precio promocional *</label>
          <input v-model.number="formPromocion.precioPromocional" type="number" min="0" step="0.01" class="ide-input" placeholder="0.00" />
        </div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:14px;">
          <div class="ide-field">
            <label>Desde *</label>
            <input v-model="formPromocion.fechaInicio" type="datetime-local" class="ide-input" />
          </div>
          <div class="ide-field">
            <label>Hasta *</label>
            <input v-model="formPromocion.fechaFin" type="datetime-local" class="ide-input" />
          </div>
        </div>
        <div v-if="editandoPromocion" style="display:flex; align-items:center; gap:10px; margin-bottom:14px;">
          <div class="ide-toggle" :class="{ 'ide-toggle--on': formPromocion.activo }" @click="formPromocion.activo = !formPromocion.activo"><div></div></div>
          <span style="font-size:12px;" :style="{ color: formPromocion.activo ? '#22c55e' : 'var(--text-faint)' }">{{ formPromocion.activo ? 'Activa' : 'Inactiva' }}</span>
        </div>
        <div style="display:flex; justify-content:flex-end; gap:8px;">
          <button @click="dialogPromocion = false" style="background:none; border:1px solid var(--border); border-radius:8px; color:var(--text-faint); padding:8px 16px; font-size:12px; cursor:pointer; font-family:inherit;">Cancelar</button>
          <button @click="guardarPromocion" :disabled="savingPromocion" style="background:#f97316; color:#fff; border:none; border-radius:8px; padding:8px 16px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit;">{{ editandoPromocion ? 'Guardar' : 'Crear' }}</button>
        </div>
      </div>
    </div>

    <!-- Modal: crear/editar sucursal -->
    <div v-if="dialogSucursal" class="cat-overlay" @click.self="dialogSucursal = false">
      <div style="background:var(--bg-surface); border:1px solid var(--border); border-radius:12px; width:460px; max-width:95%; padding:22px; max-height:90vh; overflow-y:auto;">
        <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:16px;">
          <span style="font-size:14px; font-weight:700; color:var(--text-heading);">{{ editandoSucursal ? 'Editar sucursal' : 'Nueva sucursal' }}</span>
          <button v-if="editandoSucursal" @click="eliminarSucursal" style="background:none; border:none; cursor:pointer; color:#ef4444; font-size:11px; font-family:inherit;">Eliminar</button>
        </div>
        <div style="display:grid; grid-template-columns:1fr 100px; gap:12px; margin-bottom:12px;">
          <div class="ide-field">
            <label>Nombre *</label>
            <input v-model="formSucursal.nombre" class="ide-input" placeholder="Ej: Sucursal La Paz" />
          </div>
          <div class="ide-field">
            <label>Código *</label>
            <input v-model="formSucursal.codigo" class="ide-input" placeholder="LPZ" maxlength="15" style="text-transform:uppercase;" />
          </div>
        </div>
        <div class="ide-field" style="margin-bottom:12px;">
          <label>Dirección</label>
          <input v-model="formSucursal.direccion" class="ide-input" placeholder="Av. Ejemplo #123" />
        </div>
        <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:14px;">
          <div class="ide-field">
            <label>Latitud</label>
            <input v-model.number="formSucursal.latitud" type="number" step="0.0000001" class="ide-input" placeholder="-16.5000000" />
          </div>
          <div class="ide-field">
            <label>Longitud</label>
            <input v-model.number="formSucursal.longitud" type="number" step="0.0000001" class="ide-input" placeholder="-68.1500000" />
          </div>
        </div>
        <div v-if="formSucursal.latitud && formSucursal.longitud" style="margin-bottom:14px;">
          <a :href="`https://www.google.com/maps?q=${formSucursal.latitud},${formSucursal.longitud}`" target="_blank" rel="noopener" style="font-size:11px; color:#818cf8;">Ver en Google Maps ↗</a>
        </div>

        <div style="font-size:12px; font-weight:700; color:var(--text-primary); margin-bottom:10px; border-top:1px solid var(--border); padding-top:14px;">Métodos de pago</div>
        <div style="display:flex; gap:20px; margin-bottom:14px;">
          <label style="display:flex; align-items:center; gap:6px; font-size:12px; color:var(--text-muted); cursor:pointer;">
            <input type="checkbox" v-model="formSucursal.aceptaPagoQr" /> Acepta QR
          </label>
          <label style="display:flex; align-items:center; gap:6px; font-size:12px; color:var(--text-muted); cursor:pointer;">
            <input type="checkbox" v-model="formSucursal.aceptaPagoEfectivo" /> Acepta efectivo
          </label>
        </div>

        <div v-if="editandoSucursal && formSucursal.aceptaPagoQr" style="margin-bottom:14px;">
          <div style="font-size:12px; font-weight:700; color:var(--text-primary); margin-bottom:8px;">Imagen del QR de cobro</div>
          <div style="display:flex; align-items:center; gap:12px;">
            <div style="width:72px; height:72px; border-radius:8px; overflow:hidden; background:var(--bg-page); flex-shrink:0;">
              <img v-if="editandoSucursal.qrImagenUrl" :src="editandoSucursal.qrImagenUrl" style="width:100%; height:100%; object-fit:contain;" />
            </div>
            <label class="img-upload-zone" style="flex:1;">
              <input type="file" accept="image/*" style="display:none;" @change="subirQrSucursal" />
              <span style="font-size:11px; color:var(--text-faint);">{{ subiendoQr ? 'Subiendo…' : 'Seleccionar imagen del QR' }}</span>
            </label>
          </div>
        </div>

        <div v-if="editandoSucursal" style="display:flex; align-items:center; gap:10px; margin-bottom:14px;">
          <div class="ide-toggle" :class="{ 'ide-toggle--on': formSucursal.activo }" @click="formSucursal.activo = !formSucursal.activo"><div></div></div>
          <span style="font-size:12px;" :style="{ color: formSucursal.activo ? '#22c55e' : 'var(--text-faint)' }">{{ formSucursal.activo ? 'Activa' : 'Inactiva' }}</span>
        </div>

        <div style="display:flex; justify-content:flex-end; gap:8px;">
          <button @click="dialogSucursal = false" style="background:none; border:1px solid var(--border); border-radius:8px; color:var(--text-faint); padding:8px 16px; font-size:12px; cursor:pointer; font-family:inherit;">Cancelar</button>
          <button @click="guardarSucursal" :disabled="savingSucursal" style="background:#6366f1; color:#fff; border:none; border-radius:8px; padding:8px 16px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit;">{{ editandoSucursal ? 'Guardar' : 'Crear' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
let idCounter = 0;
const nuevoId = () => `t${Date.now()}${idCounter++}`;

const formVacio = () => ({ nombre: '', descripcion: '', categoria: '', imagenUrl: '', precio: null, moneda: 'Bs', activo: true, gruposOpciones: [] });
const formCategoriaVacio = () => ({ nombre: '', orden: 0 });
const formPromocionVacio = () => ({ articuloId: '', sucursalId: '', precioPromocional: null, fechaInicio: '', fechaFin: '', activo: true });
const formSucursalVacio = () => ({ nombre: '', codigo: '', direccion: '', latitud: null, longitud: null, aceptaPagoQr: true, aceptaPagoEfectivo: true, activo: true });

export default {
  name: 'TiendaOnline',
  data() {
    return {
      tab: 'articulos',
      loading: false,
      saving: false,
      subiendoImagen: false,
      subiendoPortada: false,
      articulos: [],
      cliente: {},
      colorPrimario: '#2563eb',
      dialogForm: false,
      dialogEliminar: false,
      editando: null,
      filtroPedidos: null,
      form: formVacio(),

      categoriasTienda: [],
      dialogCategoria: false,
      savingCategoria: false,
      subiendoImagenCategoria: false,
      editandoCategoria: null,
      formCategoria: formCategoriaVacio(),

      promociones: [],
      dialogPromocion: false,
      savingPromocion: false,
      editandoPromocion: null,
      formPromocion: formPromocionVacio(),

      sucursalesTienda: [],
      dialogSucursal: false,
      savingSucursal: false,
      subiendoQr: false,
      editandoSucursal: null,
      formSucursal: formSucursalVacio(),

      disponibilidad: [],
      savingDisponibilidad: false,
    };
  },
  computed: {
    clienteId() {
      return this.$store.getters.clienteId || this.$storage.get('user')?.clienteId || null;
    },
    // Nombres de categorías creadas + la que ya tenga el artículo (si no coincide con
    // ninguna) — así el select nunca pisa en silencio una categoría vieja/con typo al
    // guardar, aunque no exista todavía como CategoriaTienda.
    opcionesCategoriaForm() {
      const nombres = this.categoriasTienda.map(c => c.nombre);
      if (this.form.categoria && !nombres.includes(this.form.categoria)) nombres.push(this.form.categoria);
      return nombres;
    },
    promoVigentePorArticulo() {
      const ahora = new Date();
      const mapa = {};
      for (const p of this.promociones) {
        if (p.activo && new Date(p.fechaInicio) <= ahora && new Date(p.fechaFin) >= ahora) mapa[p.articuloId] = p;
      }
      return mapa;
    },
  },
  async mounted() {
    await Promise.all([this.cargar(), this.cargarCliente(), this.cargarCategorias(), this.cargarPromociones(), this.cargarSucursales()]);
  },
  methods: {
    tabStyle(id) {
      const activo = this.tab === id;
      return {
        background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        padding: '10px 16px', fontSize: '12px', fontWeight: activo ? '700' : '500',
        color: activo ? '#818cf8' : 'var(--text-faint)',
        borderBottom: activo ? '2px solid #6366f1' : '2px solid transparent',
        marginBottom: '-1px',
      };
    },
    async cargar() {
      this.loading = true;
      try {
        this.articulos = await this.$service.list('tienda') || [];
      } finally {
        this.loading = false;
      }
    },
    async cargarCliente() {
      if (!this.clienteId) return;
      try {
        this.cliente = await this.$service.get('clientes', this.clienteId) || {};
        this.colorPrimario = this.cliente.tiendaColorPrimario || '#2563eb';
      } catch (e) { /* interceptor ya notifica */ }
    },
    async toggleTienda() {
      if (!this.clienteId) return;
      try {
        const actualizado = await this.$service.put(`clientes/${this.clienteId}`, { ...this.cliente, tiendaActiva: !this.cliente.tiendaActiva });
        this.cliente = actualizado;
        this.$message.success(this.cliente.tiendaActiva ? 'Tienda activada' : 'Tienda desactivada');
      } catch (e) { /* interceptor ya notifica */ }
    },
    async guardarColor() {
      if (!this.clienteId) return;
      try {
        const actualizado = await this.$service.put(`clientes/${this.clienteId}`, { ...this.cliente, tiendaColorPrimario: this.colorPrimario });
        this.cliente = actualizado;
        this.$message.success('Color actualizado');
      } catch (e) { /* interceptor ya notifica */ }
    },
    copiarLink() {
      const url = `${window.location.origin}/tienda/${this.cliente.slug}`;
      navigator.clipboard.writeText(url);
      this.$message.success('Link copiado');
    },
    async subirPortada(e) {
      const file = e.target.files[0];
      e.target.value = '';
      if (!file || !this.clienteId) return;
      this.subiendoPortada = true;
      try {
        const formData = new FormData();
        formData.append('imagen', file);
        const res = await this.$http.post(`${this.$baseServer}tienda/portada/${this.clienteId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data && res.data.datos) this.cliente = res.data.datos;
      } catch (e) {
        this.$message.error('Error al subir la portada');
      } finally {
        this.subiendoPortada = false;
      }
    },
    async abrirForm(articulo) {
      this.editando = articulo || null;
      this.form = articulo
        ? {
            nombre: articulo.nombre || '', descripcion: articulo.descripcion || '', categoria: articulo.categoria || '',
            precio: articulo.precio != null ? Number(articulo.precio) : null, moneda: articulo.moneda || 'Bs', activo: articulo.activo,
            gruposOpciones: JSON.parse(JSON.stringify(articulo.gruposOpciones || [])),
          }
        : formVacio();
      this.dialogForm = true;
      this.disponibilidad = [];
      if (articulo && this.sucursalesTienda.length) await this.cargarDisponibilidad(articulo.id);
    },
    agregarGrupo() {
      this.form.gruposOpciones.push({ id: nuevoId(), nombre: '', tipo: 'unica', obligatorio: false, min: 0, max: 1, opciones: [] });
    },
    agregarOpcion(grupo) {
      grupo.opciones.push({ id: nuevoId(), nombre: '', precioExtra: 0 });
    },
    async guardar() {
      if (!this.form.nombre) { this.$message.error('El nombre es obligatorio'); return; }
      if (this.form.precio == null || this.form.precio === '') { this.$message.error('El precio es obligatorio'); return; }
      this.saving = true;
      try {
        if (this.editando) {
          const actualizado = await this.$service.put(`tienda/${this.editando.id}`, this.form);
          const idx = this.articulos.findIndex(a => a.id === this.editando.id);
          if (idx !== -1) { this.articulos.splice(idx, 1, actualizado); this.editando = actualizado; }
          this.$message.success('Artículo actualizado');
        } else {
          const creado = await this.$service.post('tienda', this.form);
          this.articulos.unshift(creado);
          this.editando = creado;
          if (this.sucursalesTienda.length) await this.cargarDisponibilidad(creado.id);
          this.$message.success('Artículo creado — ahora podés subir la imagen');
        }
      } catch (e) {
        this.$message.error('Error al guardar');
      } finally {
        this.saving = false;
      }
    },
    confirmarEliminar() {
      this.dialogEliminar = true;
    },
    async eliminar() {
      try {
        await this.$service.delete(`tienda/${this.editando.id}`);
        this.articulos = this.articulos.filter(a => a.id !== this.editando.id);
        this.$message.success('Artículo eliminado');
        this.dialogEliminar = false;
        this.dialogForm = false;
        this.editando = null;
      } catch (e) {
        this.$message.error('Error al eliminar');
      }
    },
    async subirImagenArticulo(e) {
      const file = e.target.files[0];
      e.target.value = '';
      if (!file || !this.editando) return;
      this.subiendoImagen = true;
      try {
        const formData = new FormData();
        formData.append('imagen', file);
        const res = await this.$http.post(`${this.$baseServer}tienda/${this.editando.id}/imagen`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const actualizado = res.data && res.data.datos;
        if (actualizado) {
          const idx = this.articulos.findIndex(a => a.id === this.editando.id);
          if (idx !== -1) this.articulos.splice(idx, 1, actualizado);
          this.editando = actualizado;
        }
      } catch (e) {
        this.$message.error('Error al subir la imagen');
      } finally {
        this.subiendoImagen = false;
      }
    },

    // ── Categorías ──
    async cargarCategorias() {
      this.categoriasTienda = await this.$service.list('tienda/categorias') || [];
    },
    abrirFormCategoria(categoria) {
      this.editandoCategoria = categoria || null;
      this.formCategoria = categoria ? { nombre: categoria.nombre, orden: categoria.orden || 0 } : formCategoriaVacio();
      this.dialogCategoria = true;
    },
    async guardarCategoria() {
      if (!this.formCategoria.nombre) { this.$message.error('El nombre es obligatorio'); return; }
      this.savingCategoria = true;
      try {
        if (this.editandoCategoria) {
          const actualizado = await this.$service.put(`tienda/categorias/${this.editandoCategoria.id}`, this.formCategoria);
          const idx = this.categoriasTienda.findIndex(c => c.id === this.editandoCategoria.id);
          if (idx !== -1) { this.categoriasTienda.splice(idx, 1, actualizado); this.editandoCategoria = actualizado; }
          this.$message.success('Categoría actualizada');
        } else {
          const creada = await this.$service.post('tienda/categorias', this.formCategoria);
          this.categoriasTienda.push(creada);
          this.editandoCategoria = creada;
          this.$message.success('Categoría creada — ahora podés subir la imagen');
        }
      } catch (e) {
        this.$message.error('Error al guardar la categoría');
      } finally {
        this.savingCategoria = false;
      }
    },
    async eliminarCategoria() {
      try {
        await this.$service.delete(`tienda/categorias/${this.editandoCategoria.id}`);
        this.categoriasTienda = this.categoriasTienda.filter(c => c.id !== this.editandoCategoria.id);
        this.$message.success('Categoría eliminada');
        this.dialogCategoria = false;
        this.editandoCategoria = null;
      } catch (e) {
        this.$message.error('Error al eliminar');
      }
    },
    async subirImagenCategoria(e) {
      const file = e.target.files[0];
      e.target.value = '';
      if (!file || !this.editandoCategoria) return;
      this.subiendoImagenCategoria = true;
      try {
        const formData = new FormData();
        formData.append('imagen', file);
        const res = await this.$http.post(`${this.$baseServer}tienda/categorias/${this.editandoCategoria.id}/imagen`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const actualizado = res.data && res.data.datos;
        if (actualizado) {
          const idx = this.categoriasTienda.findIndex(c => c.id === this.editandoCategoria.id);
          if (idx !== -1) this.categoriasTienda.splice(idx, 1, actualizado);
          this.editandoCategoria = actualizado;
        }
      } catch (e) {
        this.$message.error('Error al subir la imagen');
      } finally {
        this.subiendoImagenCategoria = false;
      }
    },

    // ── Promociones ──
    nombreArticulo(articuloId) {
      const a = this.articulos.find(x => x.id === articuloId);
      return a ? a.nombre : '—';
    },
    formatearFecha(iso) {
      return new Date(iso).toLocaleString('es-BO', { timeZone: 'America/La_Paz', dateStyle: 'medium', timeStyle: 'short' });
    },
    paraInputLocal(iso) {
      if (!iso) return '';
      const partes = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/La_Paz', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(new Date(iso));
      const v = t => partes.find(p => p.type === t)?.value ?? '00';
      return `${v('year')}-${v('month')}-${v('day')}T${v('hour')}:${v('minute')}`;
    },
    estadoPromoLabel(p) {
      const ahora = new Date();
      if (!p.activo) return 'Inactiva';
      if (new Date(p.fechaFin) < ahora) return 'Vencida';
      if (new Date(p.fechaInicio) > ahora) return 'Programada';
      return 'Vigente';
    },
    estadoPromoBadgeStyle(p) {
      const estado = this.estadoPromoLabel(p);
      const color = { Vigente: '#22c55e', Programada: '#6366f1', Vencida: '#9ca3af', Inactiva: '#ef4444' }[estado];
      return { background: `${color}22`, color, padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' };
    },
    async cargarPromociones() {
      this.promociones = await this.$service.list('tienda/promociones') || [];
    },
    abrirFormPromocion(promocion) {
      this.editandoPromocion = promocion || null;
      this.formPromocion = promocion
        ? {
            articuloId: promocion.articuloId, precioPromocional: Number(promocion.precioPromocional),
            fechaInicio: this.paraInputLocal(promocion.fechaInicio), fechaFin: this.paraInputLocal(promocion.fechaFin),
            activo: promocion.activo,
          }
        : formPromocionVacio();
      this.dialogPromocion = true;
    },
    async guardarPromocion() {
      if (!this.formPromocion.articuloId) { this.$message.error('Elegí un artículo'); return; }
      if (this.formPromocion.precioPromocional == null || this.formPromocion.precioPromocional === '') { this.$message.error('El precio promocional es obligatorio'); return; }
      if (!this.formPromocion.fechaInicio || !this.formPromocion.fechaFin) { this.$message.error('Definí desde/hasta'); return; }
      this.savingPromocion = true;
      try {
        if (this.editandoPromocion) {
          const actualizada = await this.$service.put(`tienda/promociones/${this.editandoPromocion.id}`, this.formPromocion);
          const idx = this.promociones.findIndex(p => p.id === this.editandoPromocion.id);
          if (idx !== -1) this.promociones.splice(idx, 1, actualizada);
          this.$message.success('Promoción actualizada');
        } else {
          const creada = await this.$service.post('tienda/promociones', this.formPromocion);
          this.promociones.push(creada);
          this.$message.success('Promoción creada');
        }
        this.dialogPromocion = false;
      } catch (e) {
        this.$message.error(e?.response?.data?.mensaje || 'Error al guardar la promoción');
      } finally {
        this.savingPromocion = false;
      }
    },
    async eliminarPromocion() {
      try {
        await this.$service.delete(`tienda/promociones/${this.editandoPromocion.id}`);
        this.promociones = this.promociones.filter(p => p.id !== this.editandoPromocion.id);
        this.$message.success('Promoción eliminada');
        this.dialogPromocion = false;
        this.editandoPromocion = null;
      } catch (e) {
        this.$message.error('Error al eliminar');
      }
    },

    // ── Sucursales ──
    async cargarSucursales() {
      this.sucursalesTienda = await this.$service.list('tienda/sucursales') || [];
    },
    nombreSucursal(sucursalId) {
      const s = this.sucursalesTienda.find(x => x.id === sucursalId);
      return s ? s.nombre : '—';
    },
    abrirFormSucursal(sucursal) {
      this.editandoSucursal = sucursal || null;
      this.formSucursal = sucursal
        ? {
            nombre: sucursal.nombre, codigo: sucursal.codigo, direccion: sucursal.direccion || '',
            latitud: sucursal.latitud != null ? Number(sucursal.latitud) : null,
            longitud: sucursal.longitud != null ? Number(sucursal.longitud) : null,
            aceptaPagoQr: sucursal.aceptaPagoQr, aceptaPagoEfectivo: sucursal.aceptaPagoEfectivo, activo: sucursal.activo,
          }
        : formSucursalVacio();
      this.dialogSucursal = true;
    },
    async guardarSucursal() {
      if (!this.formSucursal.nombre) { this.$message.error('El nombre es obligatorio'); return; }
      if (!this.formSucursal.codigo) { this.$message.error('El código es obligatorio'); return; }
      this.savingSucursal = true;
      try {
        if (this.editandoSucursal) {
          const actualizada = await this.$service.put(`tienda/sucursales/${this.editandoSucursal.id}`, this.formSucursal);
          const idx = this.sucursalesTienda.findIndex(s => s.id === this.editandoSucursal.id);
          if (idx !== -1) { this.sucursalesTienda.splice(idx, 1, actualizada); this.editandoSucursal = actualizada; }
          this.$message.success('Sucursal actualizada');
        } else {
          const creada = await this.$service.post('tienda/sucursales', this.formSucursal);
          this.sucursalesTienda.push(creada);
          this.editandoSucursal = creada;
          this.$message.success('Sucursal creada — ahora podés subir el QR de cobro');
        }
        this.dialogSucursal = false;
      } catch (e) {
        this.$message.error(e?.response?.data?.mensaje || 'Error al guardar la sucursal');
      } finally {
        this.savingSucursal = false;
      }
    },
    async eliminarSucursal() {
      try {
        await this.$service.delete(`tienda/sucursales/${this.editandoSucursal.id}`);
        this.sucursalesTienda = this.sucursalesTienda.filter(s => s.id !== this.editandoSucursal.id);
        this.$message.success('Sucursal eliminada');
        this.dialogSucursal = false;
        this.editandoSucursal = null;
      } catch (e) {
        this.$message.error('Error al eliminar');
      }
    },
    async subirQrSucursal(e) {
      const file = e.target.files[0];
      e.target.value = '';
      if (!file || !this.editandoSucursal) return;
      this.subiendoQr = true;
      try {
        const formData = new FormData();
        formData.append('imagen', file);
        const res = await this.$http.post(`${this.$baseServer}tienda/sucursales/${this.editandoSucursal.id}/qr`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const actualizada = res.data && res.data.datos;
        if (actualizada) {
          const idx = this.sucursalesTienda.findIndex(s => s.id === this.editandoSucursal.id);
          if (idx !== -1) this.sucursalesTienda.splice(idx, 1, actualizada);
          this.editandoSucursal = actualizada;
        }
      } catch (e) {
        this.$message.error('Error al subir el QR');
      } finally {
        this.subiendoQr = false;
      }
    },

    // ── Disponibilidad por sucursal (dentro del modal de artículo) ──
    async cargarDisponibilidad(articuloId) {
      const filas = await this.$service.list(`tienda/${articuloId}/disponibilidad`) || [];
      this.disponibilidad = this.sucursalesTienda.map(s => {
        const existente = filas.find(f => f.sucursalId === s.id);
        return { sucursalId: s.id, activo: existente ? existente.activo : true, stock: existente ? existente.stock : null };
      });
    },
    async guardarDisponibilidad() {
      if (!this.editando) return;
      this.savingDisponibilidad = true;
      try {
        await this.$service.put(`tienda/${this.editando.id}/disponibilidad`, this.disponibilidad);
        this.$message.success('Disponibilidad guardada');
      } catch (e) {
        this.$message.error('Error al guardar la disponibilidad');
      } finally {
        this.savingDisponibilidad = false;
      }
    },
    filtroPedidosStyle(estado) {
      const activo = this.filtroPedidos === estado;
      return {
        background: activo ? '#818cf8' : 'transparent',
        color: activo ? 'white' : 'var(--text-muted)',
        border: `1px solid ${activo ? '#818cf8' : 'var(--border)'}`,
        borderRadius: '6px',
        padding: '6px 12px',
        fontSize: '12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontWeight: activo ? '600' : '400',
        transition: '0.2s',
      };
    },
  },
};
</script>

<style scoped>
.btn-add {
  display: flex; align-items: center; gap: 6px;
  background: #6366f1; color: #fff; border: none; border-radius: 8px;
  padding: 9px 16px; font-size: 12px; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif; flex-shrink: 0; transition: background 0.15s;
}
.cat-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.65); z-index: 9999;
  display: flex; align-items: center; justify-content: center;
}
.cat-modal {
  background: var(--bg-surface); border: 1px solid var(--border); border-radius: 12px;
  width: 640px; max-width: 95vw; max-height: 90vh;
  display: flex; flex-direction: column; overflow: hidden;
}
.img-upload-zone {
  display: flex; align-items: center; justify-content: center;
  border: 1.5px dashed var(--border); border-radius: 8px; padding: 12px;
  cursor: pointer; transition: border-color 0.15s;
}
.img-upload-zone:hover { border-color: #6366f1; }
</style>
